import { Trans, useLingui } from "@lingui/react";
import { Button } from "@warp-ds/react";
import type { FC, MouseEventHandler, ReactNode } from "react";
import { createPortal } from "react-dom";
import ReactFocusLock from "react-focus-lock";
import { useLockScroll } from "src/client/hooks/useLockScroll.ts";
import { Icon } from "../Icon.tsx";
import { Overview } from "./Overview.tsx";
import { useGallery } from "./state.tsx";

const FocusLock = ReactFocusLock as unknown as FC<{ children: ReactNode }>;

export const Fullscreen = ({ children }: { children?: ReactNode }): ReactNode => {
  const { i18n } = useLingui();
  const { setFullScreen, showNextImage, overview, toggleOverview } = useGallery();

  useLockScroll();

  const closeFullScreen: MouseEventHandler = (event) => {
    event.stopPropagation();
    setFullScreen(false);
  };

  const nextImage: MouseEventHandler = (event) => {
    if (!overview) {
      showNextImage(event);
    }
  };

  return createPortal(
    <FocusLock>
      {/** biome-ignore lint/a11y/useFocusableInteractive: button descendant */}
      {/** biome-ignore lint/a11y/useKeyWithClickEvents: button descendant */}
      {/** biome-ignore lint/a11y/useSemanticElements: button descendant */}
      <div
        role="button"
        data-testid="image-gallery-fullscreen"
        aria-label={
          !overview ? i18n.t({ id: "itemPageLayout.imageGallery.gallery.next", message: "Go to next image" }) : ""
        }
        className="fixed w-full h-full z-20 top-0 left-0 flex flex-col bg-[--w-black/90]"
        onClick={nextImage}
      >
        <div className="flex justify-between m-8">
          <Button
            quiet
            className="mt-8 !s-text-inverted-static flex gap-10 items-center !hover:s-text-placeholder !hover:bg-transparent"
            onClick={toggleOverview}
          >
            {overview ? (
              <div className="flex items-center justify-between gap-8">
                <Icon name="Image" size="medium" />
                <Trans id="itemPageLayout.imageGallery.fullscreen.big-images" message="Big images" />
              </div>
            ) : (
              <div className="flex items-center gap-8">
                <Icon name="Grid" size="medium" />
                <Trans id="itemPageLayout.imageGallery.fullscreen.image-overview" message="Image overview" />
              </div>
            )}
          </Button>
          <Button
            aria-label={i18n.t({ id: "itemPageLayout.imageGallery.fullscreen.close", message: "Close" })}
            quiet
            className="!s-text-inverted-static !hover:s-text-placeholder !hover:bg-transparent"
            onClick={closeFullScreen}
          >
            <Icon name="Close" size="medium" className="block" />
          </Button>
        </div>
        {overview ? <Overview /> : children}
      </div>
    </FocusLock>,
    document.body,
  );
};
